import { atom, signal } from "kho";
import type { Group } from "three";

/**
 * Instance model — ported from poc/editor data/placement.ts (Z-up here).
 * An object is a PARENT pivot on the surface (`position`) with a CHILD that can
 * be lifted (`height` along `up`) and freely oriented (`quaternion`). `baseYaw`
 * spins the base plate / directed arrows. Rendering is DATA-DRIVEN: the gizmo
 * only emits $transformRequest; instancesSystem is the sole writer of the mesh.
 */
export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number];

export type Instance = {
  id: string;
  /** IFC file URL (the client keeps IFC — not GLB templates). */
  url: string;
  /** DB id when this instance is a persisted object; null for a fresh one. */
  committedId: string | null;
  /** PARENT pivot: bottom-center point ON the surface. */
  position: Vec3;
  /** Final render orientation of the object — child tilt included. */
  quaternion: Quat;
  /** Surface normal at the pivot — the base plate's plane. */
  up: Vec3;
  /** Base plate rotation around `up`. */
  baseYaw: number;
  /** Child lift along `up`. */
  height: number;
  /** Uniform scale. */
  scale: number;
};

export type EditorMode = "idle" | "selected" | "place" | "drag";
export const $editorMode = atom<EditorMode>("idle");

/** All placed instances (plain data). Sole writer: instancesSystem. */
export const $instances = atom<Map<string, Instance>>(new Map());
export const $selection = atom<string | null>(null);
/** THREE group holding instance meshes — published by instancesSystem. */
export const $instancesGroup = atom<Group | null>(null);

/** Sampling radius for surface picking (world units); null = auto. */
export const $pickRadius = atom<number | null>(null);
/** Gizmo drag is over a hole → hazard overlay, delete on release. */
export const $dragDanger = atom<boolean>(false);

/** Base-plate gizmo status — world handle positions (tests aim with these). */
export const $gizmoInfo = atom<{
  attached: boolean;
  ringCenter: Vec3 | null;
  ringRadius: number;
  quaternion: Quat | null;
  handles: Record<string, Vec3>;
}>({ attached: false, ringCenter: null, ringRadius: 0, quaternion: null, handles: {} });

/** Create an instance (a fresh placement). instancesSystem assigns the mesh. */
export const $placeRequest = signal<{
  url: string;
  position: Vec3;
  quaternion: Quat;
  up: Vec3;
  committedId?: string | null;
}>();
/** Partial transform — instancesSystem merges only the provided fields. */
export const $transformRequest = signal<{
  id: string;
  position?: Vec3;
  quaternion?: Quat;
  up?: Vec3;
  baseYaw?: number;
  height?: number;
  scale?: number;
}>();
export const $deleteRequest = signal<{ id: string }>();
